import React, { useEffect, type FC, useState } from 'react';
import { add, addDays, format, getDay, isToday, parse, isWeekend } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { Space, Text } from '@g2u/ui-kit';
import { useMutation } from 'react-query';
import { Spin } from 'antd';

import { PricingCategory, ServiceType, type JsonDataItem, type WithOptionalDataTest } from 'types';
import { getServiceSelectionRates } from 'queries';
import { useText } from 'helpers/hooks/useText';
import { Toast, parseErrors } from 'helpers';
import { BookingStore } from 'store';
import { runInAction } from 'mobx';
import Big from 'big.js';

export namespace ServiceRates {
  export type Props = {
    selectionId?: number;
    serviceType?: ServiceType;
    hasSlotsToday?: boolean;
    hasSlots?: boolean;
    isRemoteAvailableToday: boolean;
  } & WithOptionalDataTest;

  const findClosestSaturday = (currentDate: Date): Date => {
    const day = currentDate.getDay();
    if (day === 6) {
      // If today is Saturday, return the current date
      return currentDate;
    } else {
      // Calculate the days until the next Saturday
      const daysUntilSaturday = 6 - day;
      return addDays(currentDate, daysUntilSaturday);
    }
  };

  const formatPrice = (price: string, isBusiness: boolean) => {
    if (isBusiness && price !== '0.00') {
      return `${price} inc GST`;
    }
    return price;
  };

  export const $: FC<Props> = observer((props) => {
    const { serviceType, selectionId, hasSlotsToday, hasSlots, isRemoteAvailableToday } = props;
    const { setServicePrice, productSelection, customer } = BookingStore;
    const [sameDayFee, setSameDayFee] = useState<string>('0.00');
    const [minServiceFee, setMinServiceFee] = useState<string>('0.00');
    const [weekendFee, setWeekendFee] = useState<string>('0.00');
    const [halfHourFee, setHalfHourFee] = useState<string>('0.00');
    const { getText } = useText();
    const isBusiness =
      customer?.customer_type === PricingCategory.Business ||
      productSelection?.accountType === PricingCategory.Business;

    // price calcualtion
    const [prices, setPrices] = useState<JsonDataItem | null>();
    const { mutate: loadServiceRates, isLoading: loadingServiceRates } = useMutation(getServiceSelectionRates, {
      onSuccess: (data: JsonDataItem, variables) => {
        const sum = Object.values(data)
          .filter((item) => typeof item === 'string')
          .reduce((acc, value) => acc + parseFloat(value.replace(/,/g, '')), 0);
        setPrices((prevPrices: any) => ({ ...prevPrices, [variables.date]: Big(sum).toFixed(2) }));
        setHalfHourFee(data.additional_fees[0]?.total || '0.00');
        setMinServiceFee(data.min_service_fee);
        if (variables.date === format(new Date(), 'yyyy-MM-dd')) {
          setSameDayFee(data.same_day_service_fee);
        }
        if (isWeekend(parse(variables.date, 'yyyy-MM-dd', new Date()))) {
          setWeekendFee(data.weekend_service_fee);
        }
      },
      onError: (data: JsonDataItem) => {
        Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
      },
    });

    const getServiceRates = (type: ServiceType) => {
      [
        format(new Date(), 'yyyy-MM-dd'),
        format(addDays(new Date(), 1), 'yyyy-MM-dd'),
        format(findClosestSaturday(new Date() as any), 'yyyy-MM-dd'),
      ].forEach((date) => {
        loadServiceRates({
          id: selectionId,
          type,
          date,
        });
      });
    };

    const today = new Date();
    const tomorrow = addDays(today, 1);
    const nextSaturday = add(today, { days: (6 - getDay(today) + 7) % 7 });
    const saturday = isToday(today) ? nextSaturday : add(nextSaturday, { days: 7 });
    const todaysPrice = prices?.[format(today, 'yyyy-MM-dd')];
    const tomorrowsPrice = prices?.[format(tomorrow, 'yyyy-MM-dd')];
    const saturdaysPrice = prices?.[format(saturday, 'yyyy-MM-dd')];

    useEffect(() => {
      runInAction(() => {
        setServicePrice({
          minServiceFee: formatPrice(minServiceFee, isBusiness),
          todayPrice: formatPrice(todaysPrice, isBusiness),
          tomorrowPrice: formatPrice(tomorrowsPrice, isBusiness),
          weekendPrice: formatPrice(saturdaysPrice, isBusiness),
          sameDayFee: sameDayFee,
          weekendFee: weekendFee,
          halfHourFee: formatPrice(halfHourFee, isBusiness),
        });
      });
    }, [todaysPrice, tomorrowsPrice, saturdaysPrice, customer]);

    useEffect(() => {
      serviceType && getServiceRates(serviceType);
    }, [serviceType]);

    if (!hasSlots) {
      return null;
    }

    return (
      <>
        <br />
        {loadingServiceRates ? (
          <Space.$ direction={Space.Direction.Vertical} align={Space.Align.Center}>
            <Spin />
          </Space.$>
        ) : (
          <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall} className='script-box'>
            <Text.$>
              {getText('booking.technician.intro')}
              {!hasSlotsToday
                ? isRemoteAvailableToday
                  ? getText('booking.technician.noOnsiteAvailability')
                  : getText('booking.technician.noRemoteAvailability')
                : getText('booking.technician.technicianTodayCost')}
              {getText('booking.technician.alternativeText')}
              <Text.$ mark>{getText('booking.technician.technicianTomorrowCost')}</Text.$>
              {getText('booking.technician.schedulePreference')}
              {getText('booking.technician.weekendAvailability')}
              {serviceType === ServiceType.Remote && getText('booking.technician.remoteSupportDetails')}
            </Text.$>
          </Space.$>
        )}
      </>
    );
  });
}
